import { useAuthActions } from "@convex-dev/auth/react";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { ConvexError } from "convex/values";
import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScrollView, Text, View } from "react-native";
import { z } from "zod";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { useSheet } from "@/elements/components/shared/sheet-provider";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { Select } from "@/elements/components/ui/select";
import { api } from "@/elements/components/utils/convex-api";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { isIbCurriculumLabel } from "@/elements/components/utils/curriculum";

const AddChildSchema = z.object({
  firstName: z.string().min(1),
  email: z.string().email(),
  school: z.string().optional(),
  curriculum: z.string().min(1),
  programme: z.string().optional(),
  yearGroup: z.string().min(1),
  subjectIds: z.array(z.string()).min(1),
});

type AddChildSchemaType = z.infer<typeof AddChildSchema>;

const safeParse = (value: string) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(error);
    return null;
  }
};

function AddChildScreen() {
  const { signOut } = useAuthActions();
  const { child } = useLocalSearchParams();
  const childData = safeParse(child as string);
  const [parentPhone, setParentPhone] = useState<string | null>(null);

  useEffect(() => {
    const loadParentPhone = async () => {
      const phone = await AsyncStorage.getItem("phone");

      setParentPhone(phone);
    };

    loadParentPhone();
  }, []);

  const {
    control,
    setValue,
    watch,
    formState: { isValid: isFormValid },
    handleSubmit,
    setError,
  } = useForm<AddChildSchemaType>({
    resolver: zodResolver(AddChildSchema),
    mode: "onChange",
    defaultValues: {
      firstName: childData?.firstName ?? "",
      email: childData?.email ?? "",
      school: childData?.school ?? "",
      curriculum: childData?.curriculum ?? "",
      programme: childData?.programme ?? "",
      yearGroup: childData?.yearGroup ?? undefined,
      subjectIds: (childData?.subjects ?? []).map(
        (subjectData: any) => subjectData.subjectId,
      ),
    },
  });

  const { data: parentProfile } = useQuery(
    convexQuery(api.parent.onboarding.queries.getProfile, {}),
  );

  const parentProfileId = parentProfile?._id;

  const { data: curriculums = [] } = useQuery({
    ...convexQuery(api.parent.onboarding.queries.getAvailableCurriculums, {}),
  });

  const selectedCurriculumId = watch("curriculum") as Id<"curriculum"> | "";
  const selectedProgrammeId = watch("programme") as Id<"keyStages"> | "";

  const selectedCurriculum = useMemo(
    () =>
      curriculums.find(
        (curriculum) => curriculum.value === selectedCurriculumId,
      ),
    [curriculums, selectedCurriculumId],
  );

  const isIbCurriculum = useMemo(
    () => isIbCurriculumLabel(selectedCurriculum?.label),
    [selectedCurriculum],
  );

  const { data: programmes = [] } = useQuery(
    convexQuery(
      api.parent.onboarding.queries.getProgrammesForCurriculum,
      isIbCurriculum && selectedCurriculumId
        ? { curriculumId: selectedCurriculumId as Id<"curriculum"> }
        : "skip",
    ),
  );

  const yearGroupsArgs = useMemo(() => {
    if (isIbCurriculum) {
      if (!selectedProgrammeId) return "skip" as const;
      return { programmeId: selectedProgrammeId as Id<"keyStages"> };
    }
    if (!selectedCurriculumId) return "skip" as const;
    return { curriculumId: selectedCurriculumId as Id<"curriculum"> };
  }, [isIbCurriculum, selectedCurriculumId, selectedProgrammeId]);

  const { data: yearGroups = [] } = useQuery(
    convexQuery(api.parent.onboarding.queries.getYearGroupsList, yearGroupsArgs),
  );

  const selectedYearGroupId = watch("yearGroup") as Id<"yearGroups"> | "";

  const { data: curriculumYearGroups = [] } = useQuery(
    convexQuery(
      api.parent.onboarding.queries.getYearGroupsList,
      isIbCurriculum &&
        selectedCurriculumId &&
        selectedYearGroupId &&
        !selectedProgrammeId
        ? { curriculumId: selectedCurriculumId as Id<"curriculum"> }
        : "skip",
    ),
  );

  useEffect(() => {
    if (!isIbCurriculum || selectedProgrammeId || !selectedYearGroupId) return;
    const match = curriculumYearGroups.find(
      (yearGroup) => yearGroup.value === selectedYearGroupId,
    );
    if (match?.programmeId) {
      setValue("programme", match.programmeId, { shouldValidate: true });
    }
  }, [
    curriculumYearGroups,
    isIbCurriculum,
    selectedProgrammeId,
    selectedYearGroupId,
    setValue,
  ]);

  const { data: subjects = [] } = useQuery(
    convexQuery(
      api.parent.onboarding.queries.getSubjectsForYearGroup,
      selectedYearGroupId
        ? { yearGroupId: selectedYearGroupId as Id<"yearGroups"> }
        : "skip",
    ),
  );

  const { data: keyStage } = useQuery(
    convexQuery(
      api.parent.onboarding.queries.getKeyStageForYearGroup,
      selectedYearGroupId
        ? { yearGroupId: selectedYearGroupId as Id<"yearGroups"> }
        : "skip",
    ),
  );

  const { open: openSingleSelectSheet } = useSheet<any, any>(
    SheetIds.SINGLE_SELECT,
  );
  const { open: openMultiSelectSheet } = useSheet<any, any>(
    SheetIds.MULTI_SELECT,
  );

  const handleCurriculumOptionsOpen = useCallback(async () => {
    const currentCurriculum = watch("curriculum");
    const result = await openSingleSelectSheet({
      options: curriculums,
      preSelectedOption: currentCurriculum,
      title: "Curriculum system",
    });
    if (
      result?.selectedOption &&
      result.selectedOption !== currentCurriculum
    ) {
      setValue("curriculum", result.selectedOption, { shouldValidate: true });
      setValue("programme", "", { shouldValidate: true });
      setValue("yearGroup", "", { shouldValidate: true });
      setValue("subjectIds", [], { shouldValidate: true });
    }
  }, [openSingleSelectSheet, curriculums, watch, setValue]);

  const handleProgrammeOptionsOpen = useCallback(async () => {
    const currentProgramme = watch("programme");
    const result = await openSingleSelectSheet({
      options: programmes,
      preSelectedOption: currentProgramme,
      title: "Which IB programme is your child currently in?",
    });
    if (
      result?.selectedOption &&
      result.selectedOption !== currentProgramme
    ) {
      setValue("programme", result.selectedOption, { shouldValidate: true });
      setValue("yearGroup", "", { shouldValidate: true });
      setValue("subjectIds", [], { shouldValidate: true });
    }
  }, [openSingleSelectSheet, programmes, watch, setValue]);

  const handleYearGroupOptionsOpen = useCallback(async () => {
    const currentYearGroup = watch("yearGroup");
    const result = await openSingleSelectSheet({
      options: yearGroups,
      preSelectedOption: currentYearGroup,
      title: isIbCurriculum
        ? "What grade is your child in?"
        : "Year Group",
    });
    if (
      result?.selectedOption &&
      result.selectedOption !== currentYearGroup
    ) {
      setValue("subjectIds", []);
      setValue("yearGroup", result.selectedOption, { shouldValidate: true });
    }
  }, [openSingleSelectSheet, yearGroups, watch, setValue, isIbCurriculum]);

  const isNextEnabled =
    isFormValid && (!isIbCurriculum || !!selectedProgrammeId);

  const handleSubjectOptionsOpen = useCallback(async () => {
    const result = await openMultiSelectSheet({
      options: subjects,
      preSelectedOptions: watch("subjectIds"),
    });
    if (result?.selectedOptions) {
      setValue("subjectIds", result.selectedOptions, { shouldValidate: true });
    }
  }, [openMultiSelectSheet, subjects, watch, setValue]);

  const { mutateAsync: createProfileByAddingStudent } = useMutation({
    mutationFn: useConvexMutation(
      api.parent.onboarding.mutations.createProfileByAddingStudent,
    ),
  });
  const { mutateAsync: createChild } = useMutation({
    mutationFn: useConvexMutation(api.parent.onboarding.mutations.createChild),
  });
  const { mutateAsync: updateChild } = useMutation({
    mutationFn: useConvexMutation(api.parent.onboarding.mutations.updateChild),
  });
  const { data: qualificationsForYearGroup } = useQuery(
    convexQuery(
      api.parent.onboarding.queries.getAvailableQualificationsForYearGroup,
      selectedYearGroupId
        ? { yearGroupId: selectedYearGroupId as Id<"yearGroups"> }
        : "skip",
    ),
  );
  const hasQualifications = (qualificationsForYearGroup ?? []).length > 0;
  const router = useRouter();

  const handleAddChild = useCallback(
    async (data: AddChildSchemaType) => {
      try {
        if (keyStage?.value) {
          if (!hasQualifications) {
            if (!parentProfileId && data.subjectIds.length > 0) {
              const timezoneIdentifier = Intl.DateTimeFormat().resolvedOptions().timeZone;
              await createProfileByAddingStudent({
                phone: parentPhone || undefined,
                parentTimezoneIdentifier: timezoneIdentifier,
                child: {
                  firstName: data.firstName,
                  email: data.email,
                  school: data.school || undefined,
                  curriculumId: data.curriculum as Id<"curriculum">,
                  yearGroupId: data.yearGroup as Id<"yearGroups">,
                  keyStageId: keyStage.value,
                  subjects: data.subjectIds.map((subjectId) => ({
                    subjectId: subjectId as Id<"subjects">,
                  })),
                  learningPreferences: ["in_person", "remote"],
                },
                onboardingStepCompleted: "childDetails",
              });
            } else {
              if (keyStage?.value) {
                if (childData?._id) {
                  await updateChild({
                    studentId: childData._id,
                    firstName: data.firstName,
                    email: data.email,
                    school: data.school || undefined,
                    curriculumId: data.curriculum as Id<"curriculum">,
                    yearGroupId: data.yearGroup as Id<"yearGroups">,
                    keyStageId: keyStage.value,
                    subjects: data.subjectIds.map((subjectId) => ({
                      subjectId: subjectId as Id<"subjects">,
                      qualificationId: undefined,
                      examDetails: undefined,
                    })),
                    learningPreferences: ["in_person", "remote"],
                  });
                } else {
                  await createChild({
                    firstName: data.firstName,
                    email: data.email,
                    school: data.school || undefined,
                    curriculumId: data.curriculum as Id<"curriculum">,
                    yearGroupId: data.yearGroup as Id<"yearGroups">,
                    keyStageId: keyStage.value,
                    subjects: data.subjectIds.map((subject) => ({
                      subjectId: subject as Id<"subjects">,
                      qualificationId: undefined,
                      examDetails: undefined,
                    })),
                    learningPreferences: ["in_person", "remote"],
                  });
                }
              }
            }
            router.replace("/(app)/onboarding/(parent)/manage-children");
          } else {
            router.push(
              `/(app)/onboarding/(parent)/child-qualification?child=${JSON.stringify(
                {
                  studentId: childData?._id,
                  firstName: data.firstName,
                  email: data.email,
                  school: data.school || undefined,
                  curriculumId: data.curriculum as Id<"curriculum">,
                  yearGroupId: data.yearGroup as Id<"yearGroups">,
                  keyStageId: keyStage.value,
                  subjects: data.subjectIds.map((subjectId) => ({
                    subjectId: subjectId as Id<"subjects">,
                    qualificationId:
                      childData?.subjects.find(
                        (subjectData: any) =>
                          subjectData.subjectId === subjectId,
                      )?.qualificationId ?? undefined,
                    examDetails:
                      childData?.subjects.find(
                        (subjectData: any) =>
                          subjectData.subjectId === subjectId,
                      )?.examDetails ?? undefined,
                  })),
                  ...(childData?._id ? { studentId: childData._id } : {}),
                },
              )}`,
            );
          }
        }
      } catch (error) {
        if (error instanceof ConvexError) {
          const { errors } = error.data;

          if (errors) {
            Object.entries(errors).forEach(([key, value]) => {
              setError(key as keyof AddChildSchemaType, {
                message: value as string,
              });
            });
          }
        }
      } finally {
        await AsyncStorage.removeItem("phone");
      }
    },
    [
      keyStage?.value,
      hasQualifications,
      parentProfileId,
      router,
      createProfileByAddingStudent,
      childData?._id,
      updateChild,
      createChild,
      childData?.subjects,
      parentPhone,
    ],
  );

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() => (router.canGoBack() ? router.back() : signOut())}
        />
        <AppBar.Title>{router.canGoBack() ? "Back" : "Sign out"}</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Tell us about your child
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                What is your child studying now?
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-4">
              <Controller
                control={control}
                name="firstName"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="First name"
                    {...field}
                    error={!!fieldState.error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      size="md"
                      placeholder="Child's Email"
                      {...field}
                      error={!!fieldState.error?.message}
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />

                    <Input.Message className="mt-[-12px] text-xs">
                      Child's email address must be different from yours
                    </Input.Message>
                  </>
                )}
              />

              <Controller
                control={control}
                name="school"
                render={({ field }) => (
                  <Input
                    size="md"
                    placeholder="School (optional)"
                    {...field}
                    value={field.value ?? ""}
                  />
                )}
              />

              <Controller
                control={control}
                name="curriculum"
                render={({ field }) => (
                  <Select size="md" onPress={handleCurriculumOptionsOpen}>
                    <Select.Label size="md">
                      {field.value
                        ? curriculums.find(
                            (curriculum) => curriculum.value === field.value,
                          )?.label
                        : "Curriculum system"}
                    </Select.Label>
                    <Select.DownArrow />
                  </Select>
                )}
              />
              {isIbCurriculum ? (
                <Controller
                  control={control}
                  name="programme"
                  render={({ field }) => (
                    <Select size="md" onPress={handleProgrammeOptionsOpen}>
                      <Select.Label size="md">
                        {field.value
                          ? programmes.find(
                              (programme) => programme.value === field.value,
                            )?.label
                          : "Which IB programme is your child currently in?"}
                      </Select.Label>
                      <Select.DownArrow />
                    </Select>
                  )}
                />
              ) : null}
              <Controller
                control={control}
                name="yearGroup"
                render={({ field }) => (
                  <Select
                    size="md"
                    onPress={handleYearGroupOptionsOpen}
                    disabled={
                      !selectedCurriculumId ||
                      (isIbCurriculum && !selectedProgrammeId)
                    }
                  >
                    <Select.Label size="md">
                      {field.value
                        ? yearGroups.find(
                            (yearGroup) => yearGroup.value === field.value,
                          )?.label
                        : isIbCurriculum
                          ? "What grade is your child in?"
                          : "Year Group"}
                    </Select.Label>
                    <Select.DownArrow />
                  </Select>
                )}
              />
              <View className="gap-4">
                <Select
                  size="md"
                  onPress={handleSubjectOptionsOpen}
                  disabled={!watch("yearGroup")}
                >
                  <Select.Label>Interested subjects</Select.Label>
                  <Select.DownArrow />
                </Select>
                <Select.Results
                  selectedOptions={subjects.filter((option) =>
                    (watch("subjectIds") ?? []).includes(option.value),
                  )}
                  onRemove={(value) => {
                    setValue(
                      "subjectIds",
                      (watch("subjectIds") ?? []).filter(
                        (option) => option !== value,
                      ),
                      { shouldValidate: true },
                    );
                  }}
                />
              </View>
            </View>
            <View className="h-8" />
            <Button
              className="w-full"
              intent="primary"
              size="md"
              onPress={handleSubmit(handleAddChild)}
              disabled={!isNextEnabled}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default AddChildScreen;
